import { defineStore } from 'pinia'
import { ref, watch, onMounted } from 'vue'
import { userPreferencesService } from '@/services/userPreferences'
import { logError, logInfo, logDebug } from '@/utils/logger'
import { useAuthStore } from '@/stores/auth'
import axiosInstance from '@/config/axios'

export const useThemeStore = defineStore('theme', () => {
  // État
  const isDarkMode = ref(false)
  const isChangingTheme = ref(false) // Nouvel état pour indiquer que le thème est en cours de changement
  const syncPending = ref(false) // Indique si une synchronisation avec la base de données est en attente
  
  // Valeurs par défaut pour la réinitialisation
  const defaultIsDarkMode = false
  const defaultIsChangingTheme = false
  
  // Vérifier si l'utilisateur est authentifié
  const isUserAuthenticated = () => {
    const authStore = useAuthStore()
    return authStore.isAuthenticated
  }
  
  // Clé utilisée pour stocker la préférence de thème dans localStorage
  // Utiliser un nom spécial pour éviter qu'il soit supprimé lors de la déconnexion
  const THEME_STORAGE_KEY = 'app_theme_preference'
  
  // Sauvegarder la préférence de thème
  const saveThemePreference = async (themeValue) => {
    try {
      // Toujours sauvegarder dans localStorage pour assurer la persistance entre les sessions
      localStorage.setItem(THEME_STORAGE_KEY, themeValue)
      logInfo('Préférence de thème sauvegardée dans localStorage:', themeValue)
      
      // Si l'utilisateur est authentifié, sauvegarder également dans la base de données
      if (isUserAuthenticated()) {
        logDebug('Tentative de sauvegarde en base de données avec l\'utilisateur authentifié')
        const authStore = useAuthStore()
        
        if (!authStore.accessToken) {
          logError('Token d\'authentification non disponible malgré isAuthenticated=true')
          syncPending.value = true // Marquer pour synchronisation ultérieure
          return
        }
        
        logDebug('Token d\'authentification disponible:', !!authStore.accessToken)
        
        // Essayer d'enregistrer la préférence avec un nouvel appel axios explicite
        try {
          const response = await axiosInstance.post('/user-preferences/upsert/', {
            preference_key: 'theme',
            preference_value: themeValue
          }, {
            headers: {
              'Authorization': `Bearer ${authStore.accessToken}`,
              'Content-Type': 'application/json'
            }
          })
          
          if (response && response.data) {
            logInfo('Préférence de thème sauvegardée en base de données:', response.data)
            syncPending.value = false // Synchronisation réussie
          } else {
            logError('Échec de la sauvegarde en base de données: réponse invalide')
            syncPending.value = true // Marquer pour réessayer plus tard
          }
        } catch (apiError) {
          logError('Erreur lors de la sauvegarde en base de données:', apiError)
          syncPending.value = true // Marquer pour réessayer plus tard
        }
      } else {
        logDebug('Utilisateur non authentifié, sauvegarde en base de données ignorée')
        syncPending.value = true // Marquer pour synchronisation après authentification
      }
    } catch (error) {
      logError('Erreur lors de la sauvegarde de la préférence de thème:', error)
      // S'assurer que la préférence est au moins sauvegardée dans localStorage
      localStorage.setItem(THEME_STORAGE_KEY, themeValue)
    }
  }
  
  // Synchroniser les préférences avec la base de données
  const syncWithDatabase = async () => {
    if (!isUserAuthenticated()) {
      logDebug('Synchronisation ignorée: utilisateur non authentifié')
      return
    }
    
    try {
      const themeValue = localStorage.getItem(THEME_STORAGE_KEY)
      if (!themeValue) {
        logDebug('Aucune préférence de thème à synchroniser')
        return
      }
      
      logDebug('Synchronisation de la préférence de thème avec la base de données:', themeValue)
      const authStore = useAuthStore()
      
      const response = await axiosInstance.post('/user-preferences/upsert/', {
        preference_key: 'theme',
        preference_value: themeValue
      }, {
        headers: {
          'Authorization': `Bearer ${authStore.accessToken}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response && response.data) {
        logInfo('Synchronisation réussie de la préférence de thème:', response.data)
        syncPending.value = false
      }
    } catch (error) {
      logError('Erreur lors de la synchronisation avec la base de données:', error)
    }
  }
  
  // Initialiser le mode sombre en fonction des préférences de l'utilisateur
  const initializeTheme = async () => {
    try {
      let themePreference = null
      
      // Vérifier d'abord dans localStorage avec notre clé spéciale
      themePreference = localStorage.getItem(THEME_STORAGE_KEY)
      
      // Si l'utilisateur est authentifié, essayer de récupérer depuis la base de données
      if (isUserAuthenticated()) {
        try {
          const preference = await userPreferencesService.getPreference('theme')
          if (preference && preference.preference_value) {
            // Si une préférence existe en base de données, l'utiliser et mettre à jour localStorage
            // UNIQUEMENT si la préférence locale n'existe pas déjà
            if (!themePreference) {
              themePreference = preference.preference_value
              localStorage.setItem(THEME_STORAGE_KEY, themePreference)
              logInfo('Préférence récupérée depuis la base de données:', themePreference)
            } else {
              // Si la préférence locale existe, vérifier si elle est différente de celle en base
              if (themePreference !== preference.preference_value) {
                logDebug('Préférence locale différente de la base de données, conservation de la préférence locale:', themePreference)
              }
            }
          } else if (themePreference) {
            // Si une préférence existe dans localStorage mais pas en base de données, synchroniser
            logDebug('Synchronisation de la préférence localStorage vers la base de données')
            await syncWithDatabase()
          }
        } catch (dbError) {
          logError('Erreur lors de la récupération depuis la base de données:', dbError)
        }
      }
      
      // Vérifier l'ancienne clé pour la migration
      if (!themePreference) {
        themePreference = localStorage.getItem('theme')
        if (themePreference) {
          // Migrer vers la nouvelle clé
          localStorage.setItem(THEME_STORAGE_KEY, themePreference)
          localStorage.removeItem('theme')
        }
      }
      
      // Appliquer la préférence si elle existe
      if (themePreference) {
        isDarkMode.value = themePreference === 'dark'
      } else {
        // Utiliser le mode clair par défaut
        isDarkMode.value = false
        // Sauvegarder cette préférence
        const themeValue = 'light'
        if (isUserAuthenticated()) {
          await saveThemePreference(themeValue)
        } else {
          localStorage.setItem('theme', themeValue)
        }
      }
      
      // Appliquer le thème
      applyTheme()
    } catch (error) {
      logError('Erreur lors de l\'initialisation du thème:', error)
      // En cas d'erreur, utiliser le mode par défaut
      isDarkMode.value = false
      applyTheme()
    }
  }
  
  // Basculer entre les modes clair et sombre
  const toggleDarkMode = async () => {
    // Si le thème est déjà en cours de changement, ne rien faire
    if (isChangingTheme.value) return
    
    // Indiquer que le thème est en cours de changement
    isChangingTheme.value = true
    
    isDarkMode.value = !isDarkMode.value
    
    // Enregistrer la préférence
    const themeValue = isDarkMode.value ? 'dark' : 'light'
    await saveThemePreference(themeValue)
    
    // Appliquer le thème
    applyTheme()
    
    // Réactiver le bouton après un délai
    setTimeout(() => {
      isChangingTheme.value = false
    }, 1000) // Délai de 1 seconde
  }
  
  // Appliquer le thème à l'élément HTML
  const applyTheme = () => {
    if (isDarkMode.value) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }
  
  // Méthode personnalisée pour réinitialiser le store
  async function $reset() {
    // Préserver la préférence de thème actuelle au lieu de la réinitialiser
    const currentTheme = localStorage.getItem(THEME_STORAGE_KEY) || 'light'
    logDebug('Réinitialisation du store de thème, préservation de la préférence:', currentTheme)
    
    // Réinitialiser uniquement les états internes
    isChangingTheme.value = defaultIsChangingTheme
    
    // Conserver l'état du mode sombre en fonction de la préférence sauvegardée
    isDarkMode.value = currentTheme === 'dark'
    
    // Appliquer le thème préservé
    applyTheme()
  }

  // Écouter les changements d'authentification pour synchroniser les préférences
  watch(() => isUserAuthenticated(), async (isAuthenticated) => {
    if (isAuthenticated && syncPending.value) {
      logDebug('Utilisateur authentifié, tentative de synchronisation des préférences')
      await syncWithDatabase()
    }
  })
  
  return {
    isDarkMode,
    isChangingTheme,
    initializeTheme,
    toggleDarkMode,
    syncWithDatabase,
    $reset
  }
})

